// Shared helpers for the cron-job cadence controls (Repeat = Weekly | Monthly) and for the
// month-range auto-fill on the report pages.
//
// Split of responsibility with the backend, and it matters:
//   * backend = RESOLUTION only. $MONTHSTART/$MONTHEND join PARAM_TOKENS and resolve at run time.
//     It never guesses which of a report's params happen to be dates.
//   * frontend = the AUTO-FILL. Choosing Monthly rewrites the date params it was handed from
//     buildCronParams() into those tokens, and the rewritten values stay VISIBLE in the param rows.
//
// A silent server-side swap would make a saved job run something different from what its row
// shows — the exact debuggability problem the migration to visible params was meant to end.
// Visible tokens are self-documenting: the row literally reads `fromDate = $MONTHSTART`.

import { describeCronDays } from './cronDays';

/** The two cadences expectedRunTime can express. An absent frequency reads as weekly. */
export const FREQUENCIES = [
  { text: 'Weekly', value: 'weekly' },
  { text: 'Monthly', value: 'monthly' },
];

/** Default monthly day: the last working day, which is what month-end reports want. */
export const DEFAULT_MONTH_DAY = 'LW';

/**
 * Day-of-month options for a monthly job. 'LW' leads because "last working day of the month" is
 * the ask; note there is no holiday calendar anywhere in this codebase, so LW means the last
 * Mon-Fri and a month ending on a county holiday still runs on that holiday.
 */
export const MONTH_DAYS = [
  { text: 'Last working day', value: 'LW' },
  { text: 'Last day of month', value: 'L' },
  ...Array.from({ length: 31 }, (_, i) => ({ text: `Day ${i + 1}`, value: String(i + 1) })),
];

/** Every from/to date-param pair the report pages produce. The payments page covers all four. */
export const DATE_PARAM_PAIRS = [
  ['fromDate', 'toDate'],
  ['fromPaymentDate', 'toPaymentDate'],
  ['fromCCDate', 'toCCDate'],
  ['fromSystemDate', 'toSystemDate'],
];

/** A single as-of date (Credit Card Summary with outstanding-only) tracks the month end. */
export const AS_OF_DATE_KEY = 'asOfDate';

/** The month tokens the backend resolves. Kept in sync with PARAM_TOKENS in generateReports.js. */
export const MONTH_RANGE_TOKENS = ['$MONTHSTART', '$MONTHEND', '$PREVMONTHSTART', '$PREVMONTHEND'];

/** The daily convention every already-migrated job uses. */
export const TODAY_TOKEN = '$TODAY';

// True when the value is 'L', 'LW' or an integer day 1-31. Mirrors isValidMonthDayToken in the
// backend's over-night-reports/utils.js so both ends agree on what can be saved.
export const isValidMonthDay = (v) => {
  if (v === 'L' || v === 'LW') return true;
  if (typeof v !== 'string' && typeof v !== 'number') return false;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return false;
  const n = Number(s);
  return n >= 1 && n <= 31;
};

// Trim and keep as a STRING — never amountToNumber, which is what collapsed '1-5' to 1 on the
// weekly Days field (TKT-000490); the same rule applies here so 'LW' survives a save.
export const normalizeMonthDay = (v) => (isValidMonthDay(v) ? String(v).trim() : DEFAULT_MONTH_DAY);

/** A Vuetify rule usable as :rules="[monthDayRule]". */
export const monthDayRule = (v) => isValidMonthDay(v)
  || "Use a day 1-31, 'L' (last day) or 'LW' (last working day)";

/** Human-readable day-of-month, e.g. 'Last working day of month', 'Day 15 of month'. */
export const describeMonthDay = (v) => {
  if (v === 'LW') return 'Last working day of month';
  if (v === 'L') return 'Last day of month';
  if (!isValidMonthDay(v)) return 'Invalid day of month';
  return `Day ${String(v).trim()} of month`;
};

const pad = (v) => (v == null ? 0 : v).toString().padStart(2, '0');

/**
 * Summary for the job list's Expected Time column. Weekly rows delegate to describeCronDays, so
 * they read exactly as they did before monthly existed.
 * @param {Object} expectedRunTime - { frequency, days, monthDay, hrs, mins, secs }
 * @returns {string} e.g. 'Mon–Fri 22:05:00' | 'Last working day of month 03:00:00'
 */
export const describeSchedule = (expectedRunTime) => {
  const rt = expectedRunTime || {};
  const time = `${pad(rt.hrs)}:${pad(rt.mins)}:${pad(rt.secs)}`;
  const cadence = rt.frequency === 'monthly'
    ? describeMonthDay(rt.monthDay)
    : describeCronDays(rt.days);
  return `${cadence} ${time}`;
};

/**
 * Rewrite whichever date pair the report actually uses to the full-month tokens, so the saved
 * job runs the month it MEANS rather than the literal dates that were on screen.
 *
 * A pair with only one half present is NOT guessed at: the Cashiers Report takes a single date
 * (fromDate with no toDate), and inventing a toDate would silently widen it into a range. Those
 * keys are returned in `unpaired` so the dialog can say so instead.
 *
 * @param {Object} params - param rows as key/value (from buildCronParams or hand-edited)
 * @returns {{ params: Object, unpaired: string[] }} - a NEW params object; input untouched
 */
export const applyMonthRange = (params) => {
  const out = { ...(params || {}) };
  const unpaired = [];

  DATE_PARAM_PAIRS.forEach(([from, to]) => {
    const hasFrom = Object.prototype.hasOwnProperty.call(out, from);
    const hasTo = Object.prototype.hasOwnProperty.call(out, to);
    if (hasFrom && hasTo) {
      out[from] = '$MONTHSTART';
      out[to] = '$MONTHEND';
    } else if (hasFrom) {
      unpaired.push(from);
    } else if (hasTo) {
      unpaired.push(to);
    }
  });

  // Only when the report already sent one — never introduce a filter the report does not have.
  if (Object.prototype.hasOwnProperty.call(out, AS_OF_DATE_KEY)) {
    out[AS_OF_DATE_KEY] = '$MONTHEND';
  }

  return { params: out, unpaired };
};

/**
 * Undo applyMonthRange when the user switches back to Weekly: every month token becomes an
 * editable value again. Defaults to $TODAY, the convention every already-migrated daily job uses.
 *
 * Keyed on the VALUE, not on a list of date keys, so it round-trips whatever applyMonthRange
 * touched (including a key hand-typed by the user).
 *
 * @param {Object} params
 * @param {string} [fallback] - value to restore; defaults to '$TODAY'
 * @returns {Object} - a NEW params object; input untouched
 */
export const clearMonthRange = (params, fallback = TODAY_TOKEN) => {
  const out = { ...(params || {}) };
  Object.keys(out).forEach((key) => {
    if (MONTH_RANGE_TOKENS.indexOf(out[key]) !== -1) {
      out[key] = fallback;
    }
  });
  return out;
};

export default {};
